import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  UsersThree,
  Receipt,
  CalendarBlank,
  TrendUp,
  Clock,
} from "@phosphor-icons/react";
import api, { formatEUR, formatDate, CATEGORIAS } from "../lib/api";
import CategoriaBadge from "../components/CategoriaBadge";
import { useAuth } from "@/context/AuthContext";

function StatCard({
  icon: Icon,
  label,
  value,
  hint,
  accent = "primary",
  testId,
}) {
  const accentColors = {
    primary: "bg-med-primary/10 text-med-primary",
    secondary: "bg-med-secondary/10 text-med-secondary",
    success: "bg-med-success/10 text-med-success",
    warning: "bg-med-warning/15 text-med-warning",
  };
  return (
    <div
      data-testid={testId}
      className="bg-med-surface border border-med-border rounded-2xl p-6 shadow-[0_8px_30px_rgba(211,97,53,0.04)]"
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-med-muted font-semibold">
            {label}
          </div>
          <div className="font-display text-3xl font-bold text-med-text mt-2">
            {value}
          </div>
          {hint && <div className="text-xs text-med-muted mt-2">{hint}</div>}
        </div>
        <div
          className={`w-11 h-11 rounded-xl grid place-items-center ${accentColors[accent]}`}
        >
          <Icon size={22} weight="duotone" />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  // Extraemos el usuario y la función de permisos del contexto
  const { user, can } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
  }, []);

  if (!stats)
    return (
      <div className="p-8 text-med-muted" data-testid="dashboard-loading">
        Cargando…
      </div>
    );

  const totalAdeudado = stats.total_pendiente;

  // 1. Definimos todas las tarjetas posibles y su requerimiento de permiso
  const todasLasCards = [
    {
      icon: UsersThree,
      label: "Falleros censados",
      value: stats.total_falleros,
      hint: "Total activos en la falla",
      accent: "secondary",
      testId: "stat-falleros",
      permission: "view_members", // Visible para admin, tesorero, presidente, secretario, contable
    },
    {
      icon: Receipt,
      label: "Recibos pagados",
      value: stats.num_recibos_pagados,
      hint: formatEUR(stats.total_pagado) + " cobrado",
      accent: "success",
      testId: "stat-pagados",
      permission: "view_recibos", // Visible para admin, tesorero, presidente, contable
    },
    {
      icon: Clock,
      label: "Recibos pendientes",
      value: stats.num_recibos_pendientes,
      hint: formatEUR(totalAdeudado) + " por cobrar",
      accent: "warning",
      testId: "stat-pendientes",
      permission: "view_recibos", // Mismo permiso
    },
    {
      icon: TrendUp,
      label: "Total recaudado",
      value: formatEUR(stats.total_pagado),
      hint: "Ejercicio actual",
      accent: "primary",
      testId: "stat-recaudado",
      permission: "view_contabilidad", // Visible para admin, tesorero, presidente, contable
    },
  ];

  // 2. Filtramos las tarjetas basándonos en lo que este usuario puede ver
  const cardsVisibles = todasLasCards.filter((card) => can(card.permission));

  const fallero = user?.fallero;
  const nombre = fallero?.nombre || "fallero";

  return (
    <div className="p-6 md:p-10" data-testid="dashboard-page">
      <motion.header
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mb-10"
      >
        <div className="text-xs uppercase tracking-[0.2em] text-med-muted font-semibold mb-2">
          Panel principal · {stats.falla_nombre}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-med-text">
          ¡Hola, {nombre}!
        </h1>
        <p className="text-med-muted mt-2 text-base">
          {(user.roles || []).includes("fallero")
            ? "Bienvenido al portal de tu comisión."
            : "Aquí tienes el resumen de tu falla a día de hoy."}
        </p>
      </motion.header>

      {/* Renderizado dinámico de columnas del grid (de 1 a 4 según las tarjetas permitidas) */}
      {cardsVisibles.length > 0 && (
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{
            visible: { transition: { staggerChildren: 0.06 } },
          }}
          className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 mb-10"
        >
          {cardsVisibles.map((c) => (
            <motion.div
              key={c.testId}
              variants={{
                hidden: { opacity: 0, y: 10 },
                visible: { opacity: 1, y: 0 },
              }}
            >
              <StatCard {...c} />
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Ajustamos las columnas de abajo: si el usuario no ve las categorías, el bloque de eventos toma todo el ancho */}
      <div
        className={`grid grid-cols-1 gap-6 ${can("view_members") ? "lg:grid-cols-3" : "lg:grid-cols-1"}`}
      >
        {/* Distribución por categorías (Requiere ver miembros) */}
        {can("view_members") && (
          <div className="lg:col-span-2 bg-med-surface border border-med-border rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl font-bold text-med-text">
                Distribución por categorías
              </h2>
              <span className="text-xs uppercase tracking-[0.18em] text-med-muted">
                Autocalculado por edad
              </span>
            </div>
            <div className="space-y-4">
              {CATEGORIAS.map((c) => {
                const n = stats.categorias[c.key] || 0;
                const pct =
                  stats.total_falleros > 0
                    ? (n / stats.total_falleros) * 100
                    : 0;
                return (
                  <div key={c.key}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-3">
                        <CategoriaBadge categoria={c.key} />
                        <span className="text-sm text-med-muted">
                          {c.range}
                        </span>
                      </div>
                      <div className="text-sm font-semibold text-med-text">
                        {n}
                      </div>
                    </div>
                    <div className="h-2 rounded-full bg-med-surface2 overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.6 }}
                        className="h-full bg-med-primary"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Próximos eventos (Lo dejamos visible para todos, incluidos los falleros básicos) */}
        <div className="bg-med-surface border border-med-border rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-6">
            <CalendarBlank
              size={20}
              weight="duotone"
              className="text-med-secondary"
            />
            <h2 className="font-display text-2xl font-bold text-med-text">
              Próximos eventos
            </h2>
          </div>
          {stats.proximos_eventos.length === 0 ? (
            <p className="text-sm text-med-muted">
              No hay eventos próximos.{" "}
              {can("manage_eventos") && "Añade uno desde el calendario."}
            </p>
          ) : (
            <ul className="space-y-3">
              {stats.proximos_eventos.map((e) => (
                <li
                  key={e.id}
                  data-testid={`evento-prox-${e.id}`}
                  className="border-l-2 border-med-primary pl-3 py-1"
                >
                  <div className="text-sm font-semibold text-med-text">
                    {e.titulo}
                  </div>
                  <div className="text-xs text-med-muted">
                    {formatDate(e.fecha_inicio)}
                    {e.lugar ? ` · ${e.lugar}` : ""}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
